import DefaultModal from '@/src/common/components/marketing/modals/DefaultModal'
import { useState } from 'react'
import { styled, theme } from '@/src/stitches.config'
import { ClipLoader } from 'react-spinners'
import {
    ModalBodyWrapper,
    ModalButtonsWrapper,
} from '@/src/common/components/elements/Modals/StyledModalComponents'
import Button from '@/src/common/components/marketing/buttons/Button'
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons/faCircleCheck'
import { ResendRedemptionEmailInput } from '@/dto/ResendRedemptionEmailInput'
import postOrderStatusResendEmail from '@/src/common/queries/postOrderStatusResendEmail'
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons'
import ResendGiftCardModalInfo from '@/src/common/components/marketing/modals/ModalResendGiftCard/ResendModalAlertInfo'
import { ValidationError } from 'yup'
import EmailInput from '@/src/common/components/elements/Inputs/EmailInput'
import { singleRecipientEmailSchema } from '@/src/modules/build-a-card/validations/schemas'
import { FormErrorDisplay } from '@/src/modules/build-a-card/FormErrorDisplay'
import {
    format12HourTimeWithZone,
    formatFullDateWithMonthSpelled,
} from '@/src/common/utilities/dateFormatters'
import { SuccessCheck } from '../../elements/Animations/SuccessCheck'

const CenterContent = styled('div', {
    display: 'flex',
    fd: 'column',
    width: '100%',
    margin: 'auto',
    alignItems: 'center',
    rowGap: '8px',
    textAlign: 'center',
})

const StartForm = styled('form', {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
})

const ModalButtons = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    mt: 10,
    gap: 10,
})

const FormSectionHeader = styled('p', {
    fontWeight: 'bold',
    marginTop: '0',
})

const FormSectionContent = styled('p', {
    width: '100%',
})

const FakeInput = styled('span', {
    backgroundColor: '$lightest',
    color: '$dark',
    borderRadius: '$2',
    padding: '$1 $1',
    display: 'block',
    width: '100%',
    marginBottom: '$1',
})

const InputFlexContainer = styled('div', {
    display: 'flex',
    flexDirection: 'row',
    '& input': {
        width: '20px',
        height: '20px',
        marginRight: '$2',
    },
})

const HelperTextContainer = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
})

const HelpText = styled('span', {
    color: '$greyText',
    fontSize: '$2',
})

const ModalHeader = styled('div', {
    width: '100%',
    fontSize: '1.45rem !important',
    fontWeight: 'bold',
    lineHeight: '1.5 !important',
    mt: '$4',
    mb: 10,
    '@media (max-width: 455px)': {
        mt: 10,
    },
})

interface IModalResendEGiftProps {
    showModal: boolean
    setShowModal: Function
    eGiftPayload: ResendRedemptionEmailInput
    emailUpdateCallback: (cardUUID: string, newEmail: string) => void
}

export enum ModalResendEGiftState {
    START,
    CONFIRMATION,
    LOADING,
    END,
    FAILED,
}

const ResendGiftCardModal = ({
    showModal,
    setShowModal,
    eGiftPayload,
    emailUpdateCallback,
}: IModalResendEGiftProps) => {
    const [modalState, setModalState] = useState<ModalResendEGiftState>(
        ModalResendEGiftState.START
    )
    const [isResend, setIsResend] = useState<boolean>(true)
    const [email, setEmail] = useState('')
    const [formErrorsEmail, setFormErrorsEmail] = useState<
        ValidationError[] | null
    >([])
    const [currentTime, setCurrentTime] = useState('')

    const resendEGift = () => {
        const payload = {
            ...eGiftPayload,
            email_address: isResend ? null : email,
        }

        postOrderStatusResendEmail(payload)
            .then((response) => {
                const d = new Date()

                setCurrentTime(
                    `${formatFullDateWithMonthSpelled(
                        d
                    )} ${format12HourTimeWithZone(d)}`
                )
                emailUpdateCallback(payload.card_uuid, response.resentTo)
                setModalState(ModalResendEGiftState.END)
            })
            .catch((e) => {
                console.error(e)
                setModalState(ModalResendEGiftState.FAILED)
            })
    }

    const setShowModalWrapper = (val: boolean) => {
        if (val === false) {
            setModalState(ModalResendEGiftState.START)
            setEmail('')
            setCurrentTime('')
            setFormErrorsEmail(null)
            setIsResend(true)
        }

        setShowModal(val)
    }

    const validateEmail = () => {
        const validation = {
            recipientEmail: email,
        }

        singleRecipientEmailSchema
            .validate(validation, {
                abortEarly: false,
                context: {
                    checkPhoneBlocked: true,
                },
            })
            .then(() => {
                setFormErrorsEmail(null)
            })
            .catch((err) => {
                setFormErrorsEmail(err.inner)
            })
    }

    return (
        <DefaultModal
            showModal={showModal}
            setShowModal={setShowModalWrapper}
            testId={'resend-gift-card-modal'}
        >
            <>
                {modalState === ModalResendEGiftState.START ? (
                    <>
                        <ResendGiftCardModalInfo />
                        <ModalHeader style={{ marginTop: '20px' }}>
                            Resend{' '}
                            <span className={'gradient-text'}>
                                your gift card
                            </span>
                        </ModalHeader>
                        <StartForm>
                            <FormSectionHeader style={{ marginTop: 0 }}>
                                Recipient information:
                            </FormSectionHeader>
                            <FormSectionContent>
                                <InputFlexContainer>
                                    <input
                                        type="radio"
                                        id="resend-gift-card"
                                        name="change-recipient"
                                        checked={isResend}
                                        onClick={() => setIsResend(true)}
                                    />
                                    <HelperTextContainer>
                                        <label htmlFor={'resend-gift-card'}>
                                            Resent to the same email address:
                                        </label>
                                        <FakeInput>
                                            Email: {eGiftPayload.email_address}
                                        </FakeInput>
                                    </HelperTextContainer>
                                </InputFlexContainer>
                            </FormSectionContent>
                            <FormSectionHeader style={{ marginTop: 0 }}>
                                Recipient information:
                            </FormSectionHeader>
                            <FormSectionContent>
                                <InputFlexContainer>
                                    <input
                                        type="radio"
                                        id="change-recipient-gift-card"
                                        name="change-recipient"
                                        checked={!isResend}
                                        onClick={() => setIsResend(false)}
                                    />
                                    <HelperTextContainer>
                                        <label htmlFor="change-recipient-gift-card">
                                            Resend to a different email address
                                        </label>
                                        <HelpText>
                                            *You can change for a new recipient
                                            with this option
                                        </HelpText>
                                    </HelperTextContainer>
                                </InputFlexContainer>
                                {!isResend && (
                                    <div style={{ marginTop: '20px' }}>
                                        <EmailInput
                                            email={email}
                                            setEmail={setEmail}
                                            formErrors={formErrorsEmail ?? []}
                                            onBlur={validateEmail}
                                            setFormErrors={setFormErrorsEmail}
                                            fieldId={'resend-email'}
                                        />
                                        <FormErrorDisplay
                                            formErrors={formErrorsEmail ?? []}
                                            path={'recipientEmail'}
                                            className={'error'}
                                        />
                                    </div>
                                )}
                            </FormSectionContent>
                        </StartForm>
                        <ModalButtons>
                            <Button
                                label={`Next`}
                                variant={'primary'}
                                textTransform={'uppercase'}
                                width={'full'}
                                showArrow={true}
                                onClick={() => {
                                    validateEmail()
                                    if (
                                        isResend ||
                                        (!isResend &&
                                            email !== '' &&
                                            formErrorsEmail === null)
                                    ) {
                                        setModalState(
                                            ModalResendEGiftState.CONFIRMATION
                                        )
                                    }
                                }}
                            />
                            <Button
                                label={`Cancel`}
                                variant={'primaryOutline'}
                                textTransform={'uppercase'}
                                width={'full'}
                                showArrow={false}
                                onClick={() => {
                                    setShowModalWrapper(false)
                                }}
                            />
                        </ModalButtons>
                    </>
                ) : undefined}
                {modalState === ModalResendEGiftState.CONFIRMATION ? (
                    <>
                        <ResendGiftCardModalInfo />
                        <ModalHeader>Confirmation</ModalHeader>
                        <p>
                            It is important that you verify the recipient&apos;s
                            information before submitting the request. An email
                            will be sent upon submission. Please ensure that all
                            details are correct to avoid any delays or errors.
                        </p>
                        <FakeInput>
                            <span style={{ fontWeight: 'bold' }}>
                                Email Address:
                            </span>{' '}
                            {isResend ? eGiftPayload.email_address : email}
                        </FakeInput>
                        <ModalButtons>
                            <Button
                                label={`Back to Edit`}
                                variant={'primaryOutline'}
                                textTransform={'uppercase'}
                                width={'full'}
                                style={{ width: '100%' }}
                                showArrow={false}
                                onClick={() => {
                                    setModalState(ModalResendEGiftState.START)
                                }}
                            />
                            <Button
                                label={`Resend EGift Card`}
                                variant={'primary'}
                                textTransform={'uppercase'}
                                width={'full'}
                                showArrow={true}
                                onClick={() => {
                                    setModalState(ModalResendEGiftState.LOADING)
                                    resendEGift()
                                }}
                            />
                        </ModalButtons>
                    </>
                ) : undefined}
                {modalState === ModalResendEGiftState.LOADING ? (
                    <CenterContent>
                        <ClipLoader
                            size="60"
                            color={'$primary'}
                            cssOverride={{ borderWidth: '4px' }}
                        />
                        <ModalHeader>
                            We are processing your request...
                        </ModalHeader>
                        <p style={{ marginTop: 0 }}>
                            Don&apos;t close this window while your request is
                            being processed.
                        </p>
                    </CenterContent>
                ) : undefined}
                {modalState === ModalResendEGiftState.END ? (
                    <CenterContent>
                        <SuccessCheck
                            icon={faCircleCheck}
                            style={{ display: 'block' }}
                            fontSize={'3rem'}
                            color={`${theme.colors.success}`}
                        />
                        <ModalHeader>
                            Good <span className={'gradient-text'}>news!</span>
                        </ModalHeader>
                        <ModalBodyWrapper>
                            <p style={{ marginTop: 0 }}>
                                The recipient was successfully changed. An email
                                with the information from your eGift.
                            </p>
                            <FakeInput>
                                <span style={{ fontWeight: 'bold' }}>
                                    Email Address:
                                </span>{' '}
                                {isResend ? eGiftPayload.email_address : email}
                                <br />
                                <span style={{ fontWeight: 'bold' }}>
                                    Date and time:
                                </span>{' '}
                                {currentTime}
                            </FakeInput>
                        </ModalBodyWrapper>
                        <ModalButtons>
                            <Button
                                label={`Done`}
                                variant={'primary'}
                                textTransform={'uppercase'}
                                width={'full'}
                                showArrow={false}
                                onClick={() => {
                                    setShowModalWrapper(false)
                                }}
                            />
                        </ModalButtons>
                    </CenterContent>
                ) : undefined}
                {modalState === ModalResendEGiftState.FAILED ? (
                    <CenterContent>
                        <SuccessCheck
                            icon={faTriangleExclamation}
                            style={{ display: 'block' }}
                            fontSize={'3rem'}
                            color={`${theme.colors.error}`}
                        />
                        <ModalHeader>
                            Oops! The gift card resend failed
                        </ModalHeader>
                        <ModalBodyWrapper>
                            <p style={{ marginTop: 0 }}>
                                We apologize for the inconvenience. Please try
                                again shortly.
                            </p>
                            <ModalButtonsWrapper>
                                <Button
                                    label={`Done`}
                                    variant={'primary'}
                                    textTransform={'uppercase'}
                                    width={'full'}
                                    showArrow={false}
                                    onClick={() => {
                                        setShowModalWrapper(false)
                                    }}
                                />
                            </ModalButtonsWrapper>
                        </ModalBodyWrapper>
                    </CenterContent>
                ) : undefined}
            </>
        </DefaultModal>
    )
}
export default ResendGiftCardModal
